import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';

import { $getContent } from '../serialization/getContent';
import { useEditorChrome } from './context/EditorChromeContext';
import { useEditorProps } from './context/EditorPropsContext';
import { useFeatureConfig } from './context/FeaturesContext';

/**
 * Reports the editor becoming active / inactive through `onFocus` / `onBlur`, with the current value.
 * - Follows the chrome's `focused` rather than DOM focus, so using the toolbar or a menu is no blur.
 * - With multi-language, `features.multiLanguage.onFocus` / `onBlur` also get the translations.
 * - On blur the value is still the edited language's: the reset to the UI language only swaps the
 *   content on a later render.
 */
export const FocusEventsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { focused } = useEditorChrome();
  const props = useEditorProps();
  const multiLanguage = useFeatureConfig('multiLanguage');

  // Read when `focused` flips, not as dependencies: a new callback must not count as a focus change
  const latest = useRef({ props, multiLanguage });
  latest.current = { props, multiLanguage };
  const wasFocused = useRef(focused);

  useEffect(() => {
    if (wasFocused.current === focused) return;
    wasFocused.current = focused;

    const { props, multiLanguage } = latest.current;
    const value = editor.read(() => $getContent(editor, props.mode));

    if (focused) {
      props.onFocus?.(value);
      if (multiLanguage) multiLanguage.onFocus?.(multiLanguage.translations);
    } else {
      props.onBlur?.(value);
      if (multiLanguage) multiLanguage.onBlur?.(multiLanguage.translations);
    }
  }, [editor, focused]);

  return null;
};
